import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { SelectChangeEvent } from '@mui/material/Select';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import ConfirmationPopup from '../../Components/ConfirmationModal/confirmation-modal.tsx'; 
import Newpagination from '../../Components/Pagination/pagination.tsx';
import { Button, Box, Typography, TextField, InputAdornment } from '@mui/material';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import SearchIcon from '@mui/icons-material/Search';

import AddStopsModal from './add-stops-modal.tsx';
import EditStopsModal from './edit-stops-modal.tsx';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getStops, deleteStops, getCompanies, getSTMSStops, deleteSTMSStops, editSTMSStops } from './action.ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Stops = () => {
    const [stops, setStops] = useState<any[]>([]);
    const [addStopsModalOpen, setAddStopsModalOpen] = useState(false);
    const [editStopsModalOpen, setEditStopsModalOpen] = useState(false);
    const [selectedStop, setSelectedStop] = useState<any>(null);
    const [pageSize, setPageSize] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [stopToDelete, setStopToDelete] = useState<any>(null);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [companyOptions, setCompanyOptions] = useState<{id: string, label: string}[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>(() => {
        const lastSelectedCompany = localStorage.getItem('lastSelectedCompany');
        return lastSelectedCompany ? lastSelectedCompany : '';
    });
    
    const [searchTerm, setSearchTerm] = React.useState('');
    const searchTermRef = useRef('');
    
    const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
    const userRole = userDetailsFromStorage?.role?.toLowerCase();
    const isAdmin = userRole === 'platform_tech' || userRole === 'platform_ops' || userRole === 'platform_finance';
    
    const [isCompaniesLoading, setIsCompaniesLoading] = useState<boolean>(isAdmin);

    const stmsCompany = companyOptions.find((c: any) => c.label.toUpperCase() === 'STMS');
    const isSTMSSelected = stmsCompany ? selectedCompany === String(stmsCompany.id) : false;

    const fetchStopsData = useCallback(() => {
        if (isCompaniesLoading) return;

        sessionStorage.removeItem('trips_active_tab');
        
        const handleSuccess = (response: any) => {
            if (response.status === 200 && response.data && response.data.length > 0) {
                if (isSTMSSelected) {
                    let parsedStops: any[] = [];
                    response.data.forEach((item: any) => {
                        if (item.stops && Array.isArray(item.stops)) {
                            item.stops.forEach((stop: any) => {
                                parsedStops.push({
                                    ...stop,
                                    vehicle_id: item.vehicle_id,
                                    vehicle_number: item.vehicle_number,
                                    school_name: item.school_name,
                                    company_id: item.company_id
                                });
                            });
                        } else {
                            parsedStops.push(item);
                        }
                    });
                    setStops(parsedStops);
                } else {
                    setStops(response.data); 
                }
                setTotalRecords(response.total_records || 0);
            } else {
                setStops([]);
                setTotalRecords(0);
                if (response.status !== 200) {
                    toast.error(response.message || "Failed to fetch stops");
                }
            }
        };

        const handleError = (errorMessage: string) => {
            setStops([]);
            setTotalRecords(0);
            toast.error(errorMessage || "An error occurred while fetching data");
        };

        if (isSTMSSelected) {
            getSTMSStops(page, pageSize, selectedCompany, handleSuccess, handleError, '', searchTermRef.current);
        } else {
            getStops(page, pageSize, selectedCompany, handleSuccess, handleError, '');
        }
    }, [page, pageSize, selectedCompany, isSTMSSelected, isCompaniesLoading]);

    useEffect(() => {
        fetchStopsData();
    }, [fetchStopsData]);

    const handleSearchClick = () => {
        if (page === 0) {
            fetchStopsData();
        } else {
            setPage(0);
        }
    };

    const handleStatusChange = (row: any, newStatus: string) => {
        const handleSuccess = (response: any) => {
            toast.success(response.message || "Status updated successfully");
            fetchStopsData();
        };

        const handleError = (error: any) => {
            toast.error(error.message || "Failed to update status");
        };

        editSTMSStops(
            row.id,
            handleSuccess,
            handleError,
            row.stopName,
            row.address,
            newStatus,  
            row.latitude,
            row.longitude,
            row.radius,
            Number(row.company_id),
            row.vehicleId,
            row.routeId
        );
    };

    const handleInputChange = (event: any) => {
        const value = event.target.value;
        setSearchTerm(value);  
        searchTermRef.current = value; 
        if (value === '') {
            if (page === 0) {
                fetchStopsData();
            } else {
                setPage(0);
            }
        }
    };

    useEffect(() => {
        if (isAdmin) {
            getCompanies(
                100,
                0,
                null,
                (response: any) => {
                    if (response.message && Array.isArray(response.message)) {
                        const companies = response.message.map((item: any) => ({
                            id: String(item.company.id),
                            label: item.company.name,
                        }));
                        setCompanyOptions(companies);
                        const savedCompanyId = localStorage.getItem('lastSelectedCompany');
                        const isSavedValid = companies.some((c: any) => c.id === savedCompanyId);

                        if (isSavedValid && savedCompanyId) {
                            setSelectedCompany(savedCompanyId);
                        } else {
                            const stmsCompanyFallback = companies.find((c: any) => c.label.toUpperCase() === 'STMS');
                            if (stmsCompanyFallback) {
                                setSelectedCompany(stmsCompanyFallback.id);
                                localStorage.setItem('lastSelectedCompany', stmsCompanyFallback.id);
                            } else if (companies.length > 0) {
                                setSelectedCompany(companies[0].id);
                                localStorage.setItem('lastSelectedCompany', companies[0].id);
                            }
                        }
                    }
                    setIsCompaniesLoading(false); 
                },
                (errMessage: any) => {
                    setIsCompaniesLoading(false); 
                }
            );
        }
    }, [isAdmin]);

    const handleClickOpen = () => setAddStopsModalOpen(true);
    const handleCloseAdd = () => setAddStopsModalOpen(false);

    const handleClickEditOpen = (stop: any) => {
        setSelectedStop(stop);
        setEditStopsModalOpen(true);
    };
    const handleCloseEdit = () => setEditStopsModalOpen(false);

    const handleDeleteClick = (stop: any) => {
        setStopToDelete(stop);
        setConfirmationOpen(true);
    };
    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
        setStopToDelete(null);
    };

    const handleDeleteStops = () => {
        const handleSuccess = (response: any) => {
            if (response.status === 200) {
                toast.success(response.message);
                setConfirmationOpen(false);
                setStopToDelete(null);
                fetchStopsData();
            } else {
                toast.error(response.message);
            }
        };

        const handleError = (errorMessage: string) => {
            toast.error(errorMessage);
        };

        if (isSTMSSelected) {
            deleteSTMSStops(stopToDelete?.id, handleSuccess, handleError, stopToDelete?.company_id);
        } else {
            deleteStops(stopToDelete?.id, handleSuccess, handleError, stopToDelete?.company_id);
        }
    };

    const hasVehicleId = stops.some(stop => stop.vehicle_id);

    const rows = stops.map((stop, index) => ({
        id: stop.id,
        slNo: (page * pageSize) + index + 1, 
        stopName: stop.name,
        address: stop.address || '',
        radius: stop.radius_meters,
        status: stop.status,
        latitude: stop.center_lat,
        longitude: stop.center_lng,
       company_id: stop.company_id ? Number(stop.company_id) : null,
        vehicleNumber: stop.vehicle_number || '', 
        vehicleId: stop.vehicle_id || null,
        routeName: stop.route_name || '',
        routeId: stop.route_id || null,
    }));

    const columns = [
        { field: 'slNo', headerName: 'SL No', width: 100, headerAlign: 'center', align: 'center' },
        { field: 'stopName', headerName: 'Stop Name', flex: 1.5, headerAlign: 'center', align: 'center' },
        {field: 'routeName', headerName: 'Route Name', flex: 1.5, headerAlign: 'center', align: 'center' },
        {field: 'address', headerName: 'Address', flex: 2, headerAlign: 'center', align: 'center' },
        { field: 'radius', headerName: 'Radius (m)', width: 120, headerAlign: 'center', align: 'center' },
        { 
            field: 'status', 
            headerName: 'Status', 
            width: 140, 
            headerAlign: 'center', 
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const currentStatus = params.value?.toLowerCase();
                if (isSTMSSelected && currentStatus === 'pending') {
                    return (
                        <select
                            value={currentStatus}
                            onChange={(e: { target: { value: string; }; }) => handleStatusChange(params.row, e.target.value)}
                            onClick={(e: { stopPropagation: () => any; }) => e.stopPropagation()} 
                            style={{ 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                border: '1px solid #ccc', 
                                textTransform: 'capitalize', 
                                cursor: 'pointer',
                                width: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            <option value="pending" disabled>Pending</option>
                            <option value="approved">Approve</option>
                            <option value="rejected">Reject</option>
                        </select>
                    );
                }
                return <span style={{ textTransform: 'capitalize' }}>{params.value}</span>;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1, 
            headerAlign: 'center', 
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', gap: '8px' }}>
                        <IconButton color="primary" onClick={() => handleDeleteClick(params.row)}>
                            <DeleteIcon />
                        </IconButton>
                        <IconButton color="primary" onClick={() => handleClickEditOpen(params.row)}>
                            <EditIcon />
                        </IconButton>
                    </div>
                );
            },
        },
    ];

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage - 1);

    const showEntries = [
        { id: '10', label: '10' },
        { id: '20', label: '20' },
        { id: '50', label: '50' },
        { id: '100', label: '100' },
    ];

    const updateField = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (!isNaN(Number(value))) {
            setPageSize(Number(value));
            setPage(0);
        }
    };

    return (
        <>
            <Header />
            <div style={{ marginTop: "70px" }}>
                <Heading
                    title="Stops"
                    closeIcon="false"
                    leftContent={
                        <>
                            <Box display="flex" alignItems="center" marginLeft='10px'>
                                <Typography style={{ marginRight: '5px' }}>Show Entries:</Typography>
                                <Dropdown
                                    options={showEntries}
                                    selectedValue={pageSize.toString()}
                                    handleChange={(event: any) => updateField(event)}
                                    width='75px'
                                    height='25px'
                                />
                            </Box>
                             {companyOptions && companyOptions.length > 0 && (
                                    <Box sx={{ marginLeft: '10px', marginTop:{xs:'5px'} }}>
                                        <Dropdown
                                            options={companyOptions}
                                            selectedValue={selectedCompany}
                                            handleChange={(e: any) => {
                                                const newValue = String(e.target.value);
                                                setPage(0);
                                                setSelectedCompany(newValue);
                                                localStorage.setItem('lastSelectedCompany', newValue);
                                            }}
                                            placeholder="Choose Company"
                                            label=" Choose Company"
                                            width="180px"
                                            height="40px"
                                            searchable={true}
                                        />
                                    </Box>
                                )}
                        </>
                    }
                    rightContent={
                        <Box display="flex" alignItems="center" gap="10px" marginRight={"10px"}>
                             {isSTMSSelected && (
                                <TextField
                                    className="styled-textfield"
                                    variant="outlined"
                                    size="small"
                                    value={searchTerm}
                                    onChange={handleInputChange}
                                    placeholder="Search by Vehicle Number"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchClick();
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleSearchClick} edge="end">
                                                    <SearchIcon sx={{ color: 'black' }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                             )}
                            <Button className="custom-button" onClick={handleClickOpen}>
                                Add Stop
                            </Button>
                        </Box>
                    }
                />
                <Box className="table-container custom-data-grid" sx={{
                    marginLeft: { xs: '10px', sm: '180px' },
                    marginRight: '40px', position: 'relative', height: '500px'
                }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        sx={{ height: '100%', '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                        hideFooterPagination
                    />
                </Box>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                    <Newpagination
                        rows={totalRecords}
                        pageSize={pageSize}
                        page={page}
                        handlePageChange={handlePageClick}
                    />
                </div>
                <AddStopsModal 
                    open={addStopsModalOpen} 
                    onClose={handleCloseAdd} 
                    onSuccess={fetchStopsData}
                    companyOptions={companyOptions} 
                />
                <EditStopsModal 
                    open={editStopsModalOpen} 
                    onClose={handleCloseEdit} 
                    onSuccess={fetchStopsData}
                    stop={selectedStop} 
                />
            </div>
            <NavBar selected="Stops" />
            <ConfirmationPopup
                open={confirmationOpen}
                onClose={handleCloseConfirmation}
                onConfirm={handleDeleteStops}
                title="Delete Stop"
                message="Are you sure you want to delete the stop?"
            />
        </>
    );
};

export default Stops;